/**
 * Полоса вкладок консоли: «Анализ | Заметки».
 *
 * ДВА ВОПРОСА, ЗАДАВАЕМЫЕ В РАЗНОЕ ВРЕМЯ. «Что говорит спутник» читают дома, «что я видел на
 * месте» пишут и перечитывают в поле. Пока заметки были блоком внизу колонки, чтобы дописать
 * одну, приходилось прокрутить мимо ответа, доказательства, снимков и истории. Вкладка — это
 * признание, что это два сеанса работы, а не два абзаца одного.
 *
 * АКТИВНАЯ ПОМЕЧЕНА ФОРМОЙ, А НЕ ТОЛЬКО ЦВЕТОМ: охряная линейка 2 px под ярлыком плюс `--ink`
 * против `--ink-dim` у соседки. Охра здесь законна — она указывает МЕСТО, а не зовёт к
 * действию; заливкой охрой по-прежнему владеет одна кнопка экрана.
 *
 * БЕЗ РОУМИНГА СТРЕЛКАМИ, И ЭТО НЕ УПРОЩЕНИЕ. `←/→` на этом экране уже имеют владельца:
 * `PreviewControl` слушает их на `window` и шагает дату снимка отовсюду, кроме текстовых полей
 * и поверхности карты. Роуминг по APG дал бы ДВУХ владельцев одного жеста, и человек, ткнувший
 * в ярлык вкладки, потерял бы возможность листать снимки. Поэтому обе вкладки стоят в табовом
 * порядке (`tabIndex={0}` у каждой), а активируются Enter/Space/кликом — вручную, а не по
 * приходу фокуса.
 */

import { T } from "../strings";

export type ConsoleTab = "analysis" | "notes";

export const TAB_IDS: Record<ConsoleTab, { tab: string; panel: string }> = {
  analysis: { tab: "tab-analysis", panel: "panel-analysis" },
  notes: { tab: "tab-notes", panel: "panel-notes" },
};

interface Props {
  active: ConsoleTab;
  onChange: (tab: ConsoleTab) => void;
  /**
   * Сколько заметок у поля. `null` — ещё не спрашивали или список не приехал.
   *
   * ЦИФРА ПЕЧАТАЕТСЯ ТОЛЬКО ОТ ЕДИНИЦЫ. «Заметки 0» утверждало бы отсутствие раньше, чем мы
   * спросили, а на неудаче списка — вместо того, чего не знаем вовсе. Ярлык без числа не
   * утверждает ничего, и это ровно то, что мы можем сказать в обоих случаях.
   */
  count: number | null;
}

export function ConsoleTabs({ active, onChange, count }: Props) {
  const tab = (id: ConsoleTab, label: string, key: number | null) => {
    const selected = active === id;
    return (
      <button
        type="button"
        role="tab"
        id={TAB_IDS[id].tab}
        className="k-tabs__tab"
        aria-selected={selected}
        aria-controls={TAB_IDS[id].panel}
        // Обе в табовом порядке: роуминга нет, см. docstring.
        tabIndex={0}
        data-testid={`console-${TAB_IDS[id].tab}`}
        onClick={() => onChange(id)}
      >
        {label}
        {key !== null && key > 0 && (
          <span className="k-mono k-tabs__key" data-testid="console-tab-notes-count">
            {key}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="k-console__tabs k-tabs" role="tablist" aria-label={T.console.tabsLabel}>
      {tab("analysis", T.console.tabAnalysis, null)}
      {tab("notes", T.console.tabNotes, count)}
    </div>
  );
}
